// Store instances by name to allow different singleton timeout workers
const timeoutWorkerInstances: Map<string, TimeoutWorker> = new Map();

// defining the timeout worker class
class TimeoutWorker {
  name: "cart" | "checkout" | null;
  worker: Worker | null;
  duration: number;
  warningDuration: number; // not used, just for debugging
  timeElapsed: number;
  isWarningFired: boolean;
  timeoutSubscribers: (() => void)[] = [];
  onTickSubscribers: ((worker: TimeoutWorker) => void)[] = [];

  constructor() {
    this.name = null;
    this.worker = new Worker(new URL("./timeout.worker.ts", import.meta.url));
    this.duration = 0;
    this.warningDuration = 0;
    this.timeElapsed = 0;
    this.isWarningFired = false;
    this.timeoutSubscribers = [];
    this.onTickSubscribers = [];
  }

  run(action: { type: string; payload?: any }) {
    this.worker?.postMessage(action);
  }

  init(duration: number) {
    this.run({ type: "init", payload: { duration } });
  }

  start() {
    this.run({ type: "start" });
  }

  stop() {
    this.run({ type: "stop" });
  }

  resume() {
    this.run({ type: "resume" });
  }

  onTick(callback: (worker: TimeoutWorker) => void) {
    this.onTickSubscribers.push(callback);
    this.run({ type: "onTick" });
  }

  onTimeout(callback: () => void) {
    this.timeoutSubscribers.push(callback);
    this.run({ type: "subscribe" });
  }

  listen() {
    if (!this.worker) return;

    this.worker.onmessage = (event) => {
      const actionType = event?.data?.type;
      const payload = event?.data?.payload;

      if (actionType === "timeout") {
        this.timeoutSubscribers.forEach((sub) => sub());
      }

      if (actionType === "onTick") {
        this.timeElapsed = payload?.timeElapsed;
        this.onTickSubscribers.forEach((sub) => sub(this));
      }
    };
  }

  reset() {
    const instanceKey = this.name || "default";
    const timeoutWorker = timeoutWorkerInstances.get(instanceKey);

    if (timeoutWorker) {
      timeoutWorker.run({ type: "reset" });
      timeoutWorker.worker?.terminate();
      timeoutWorker.worker = null;
      timeoutWorker.timeElapsed = 0;
      timeoutWorker.duration = 0;
      timeoutWorker.warningDuration = 0;
      timeoutWorker.isWarningFired = false;
      timeoutWorker.timeoutSubscribers = [];
      timeoutWorker.onTickSubscribers = [];
      timeoutWorkerInstances.delete(instanceKey);
    }
  }
}

// creating the timeout worker instance
export function createTimeoutWorker(options: {
  name: TimeoutWorker["name"];
  duration: number;
  warningDuration: number;
  onTick?: (worker: TimeoutWorker) => void;
  onTimeout?: () => void;
  init?: boolean;
}) {
  const instanceKey = options?.name || "default";

  // Check if we already have an instance for this name
  let timeoutWorker = timeoutWorkerInstances.get(instanceKey);

  // Create new instance if none exists
  if (!timeoutWorker) {
    timeoutWorker = new TimeoutWorker();
    timeoutWorker.name = options?.name || null;
    timeoutWorker.duration = options?.duration;
    timeoutWorker.warningDuration = options?.warningDuration;

    // Store the instance
    timeoutWorkerInstances.set(instanceKey, timeoutWorker);

    // setting the callbacks and initialization
    if (options?.init) timeoutWorker.init(options?.duration);
    if (options?.onTick) timeoutWorker.onTick(options.onTick);
    if (options?.onTimeout) timeoutWorker.onTimeout(options.onTimeout);
    else timeoutWorker.reset();

    // listening for messages from the worker
    timeoutWorker.listen();
  }

  if (instanceKey == "default") return null;
  return timeoutWorker;
}
